// OWNERSHIP LAYER (new file, not vendored): adapts the Dryvn Platform
// wire contract (src/life/contract.ts, canonical per the finish-line
// ruling — backend wins every mismatch) into the view types the
// vendored Lovable screens render (src/data/types.ts). Every conversion
// here is a row in docs/phase-life/CONTRACT_DIFF.md; view-state that
// Lovable stored (daysUntil, done, day/time ints) is COMPUTED here at
// the boundary, exactly as the reconciliation ruling requires.

import type {
  AgentNudge,
  Bill,
  Briefing,
  Family,
  FamilyEvent,
  Goal,
  Habit,
  HealthMetric,
  ImportantDate,
  JournalEntry,
  Kid,
  NotificationPrefs,
  LegacyLetter,
  NudgeCategory,
  Parent,
  Reading,
  Reminder,
  ShoppingItem,
  ShoppingCategory,
  SpendingRow,
  VaultItem,
  Vehicle,
  WebhookRecord,
} from "@/data/types";

// ── Backend wire shapes (subset actually consumed) ──────────────────────────

export interface WireNudge {
  id: string;
  category: string;
  title: string;
  detail: string | null;
  proposedSlot: { start?: string; end?: string; title?: string; location?: string } | null;
  proposedAction: string;
  status: string;
  createdAt: string;
}

export interface WireHabit {
  id: string;
  name: string;
  doneToday: boolean;
  currentStreak: number;
  weekGrid: boolean[];
}

export interface WireBill {
  id: string;
  name: string;
  category: string | null;
  amountCents: number;
  frequency: string;
  dueDayOfMonth: number | null;
  nextDueDate: string | null;
  daysUntilDue: number | null;
  lastPaidDate: string | null;
  autoPay: boolean;
}

export interface WireDate {
  id: string;
  label: string;
  category: string;
  eventDate: string;
  recurYearly: boolean;
  daysUntil: number;
}

export interface WireBriefing {
  greeting: string;
  todayLabel: string;
  quote: { quoteText: string; author: string };
  nudges: WireNudge[];
  habitsToday: WireHabit[];
  billsDueSoon: WireBill[];
  comingUp: WireDate[];
  weather: { tempC: number; condition: string; high: number; low: number } | null;
  groundedSearch: { headline: string; summary: string; sourceUrl: string | null } | null;
}

export interface WireEvent {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  eventKind: string;
  source: string;
  attendees?: Array<{ familyMemberId: string; memberName: string | null }>;
}

export interface WireJournal {
  id: string;
  entryDate: string;
  entryTime: string | null;
  entryType: string;
  content: string;
  moodScore: number | null;
}

export interface WireReading {
  id: string;
  title: string;
  author: string | null;
  status: string;
  progressPct: number | null;
  rating: number | null;
  finishedAt: string | null;
}

export interface WireVehicle {
  id: string;
  nickname: string | null;
  make: string;
  model: string;
  year: number | null;
  color: string | null;
  licensePlate: string | null;
  currentOdometerKm: number | null;
  nextServiceDate: string | null;
  nextServiceType: string | null;
}

export interface WireShopping {
  id: string;
  name: string;
  quantity: string | null;
  category: string | null;
  isPurchased: boolean;
  listName: string;
}

export interface WireVault {
  id: string;
  name: string;
  category: string;
  expiryDate: string | null;
  daysUntilExpiry: number | null;
}

export interface WireHealthRecord {
  id: string;
  recordDate: string;
  metricType: string;
  valueNumeric: number | null;
  valueText: string | null;
  unit: string | null;
}

export interface WireAppointment {
  id: string;
  provider: string;
  specialty: string | null;
  startsAt: string;
  status: string;
}

export interface WirePrefs {
  localSuggestionsEnabled: boolean;
  localSuggestionsPerWeek: number;
  morningBriefAt: string | null;
  // Ruling 2 (RULED 2026-07-05): channel/cadence prefs now live in the
  // backend config row.
  smsEnabled: boolean;
  pushEnabled: boolean;
  digestCadence: "daily" | "weekly" | "off";
}

// ── Adapters ─────────────────────────────────────────────────────────────────

const NUDGE_CATEGORY: Record<string, NudgeCategory> = {
  health: "Health",
  movement: "Movement",
  connection: "Connection",
  money: "Money",
  family: "Home",
  focus: "Home",
  local: "local",
};

export function toNudge(n: WireNudge): AgentNudge {
  const slot = n.proposedSlot ?? {};
  return {
    id: n.id,
    category: NUDGE_CATEGORY[n.category] ?? "Home",
    headline: n.title,
    reason: n.detail ?? "",
    proposal: {
      action: n.proposedAction === "book" ? "book" : "remind",
      label: slot.title ?? n.title,
      when: slot.start ? fmtWhen(slot.start) : "",
      ...(slot.end && slot.start ? { duration: fmtDuration(slot.start, slot.end) } : {}),
    },
    ...(n.detail ? { detail: n.detail } : {}),
    ...(slot.start ? { date: slot.start.slice(0, 10) } : {}),
  };
}

export const toHabit = (h: WireHabit): Habit => ({
  id: h.id,
  name: h.name,
  done: h.doneToday,
  streak: h.currentStreak,
  week: h.weekGrid,
});

export function toBill(b: WireBill): Bill {
  const monthStart = new Date();
  monthStart.setDate(1);
  const paid = b.lastPaidDate !== null && b.lastPaidDate >= monthStart.toISOString().slice(0, 10);
  return {
    id: b.id,
    name: b.name,
    amountCents: b.amountCents,
    dueDay: b.dueDayOfMonth ?? (b.nextDueDate ? Number(b.nextDueDate.slice(8, 10)) : 0),
    paid,
    daysUntil: b.daysUntilDue ?? 0,
    subscription: (b.category ?? "").toLowerCase() === "subscriptions",
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function toImportantDate(d: WireDate): ImportantDate {
  const month = Number(d.eventDate.slice(5, 7)) - 1;
  return {
    id: d.id,
    title: d.label,
    date: d.eventDate,
    dateLabel: `${MONTHS[month]} ${Number(d.eventDate.slice(8, 10))}`,
    monthLabel: MONTHS[month] ?? "",
    daysUntil: d.daysUntil,
    type:
      d.category === "birthday"
        ? "birthday"
        : d.category === "anniversary"
          ? "anniversary"
          : "reminder",
  };
}

const EVENT_KIND: Record<string, FamilyEvent["kind"]> = {
  school: "school",
  sports: "sports",
  carpool: "carpool",
  appointment: "meeting",
  family: "family",
  date_night: "family",
  other: "family",
};

/** Ruling in action: day int + 'HH:MM' + local date are COMPUTED from TIMESTAMPTZ. */
export function toFamilyEvent(e: WireEvent): FamilyEvent {
  const dt = new Date(e.startsAt);
  const attendeeIds = (e.attendees ?? []).map((a) => a.familyMemberId);
  return {
    id: e.id,
    title: e.title,
    memberId: attendeeIds[0] ?? "",
    day: (dt.getDay() + 6) % 7, // 0=Mon..6=Sun
    time: `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`,
    kind: EVENT_KIND[e.eventKind] ?? "family",
    date: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`,
    startsAt: e.startsAt,
    attendeeIds,
  };
}

export const toJournalEntry = (j: WireJournal): JournalEntry => ({
  id: j.id,
  date: j.entryDate,
  // 'HH:MM' — entry_time now stamped on the org clock (BUG-102 fix).
  ...(j.entryTime ? { time: j.entryTime.slice(0, 5) } : {}),
  mood: j.moodScore ?? 5,
  ...(j.entryType === "gratitude" ? { gratitude: j.content } : {}),
  note: j.content,
});

export const toReading = (r: WireReading): Reading => ({
  id: r.id,
  title: r.title,
  author: r.author ?? "",
  status: r.status === "want_to_read" ? "want" : r.status === "reading" ? "reading" : "finished",
  ...(r.progressPct !== null ? { progress: r.progressPct } : {}),
  ...(r.rating !== null ? { rating: r.rating } : {}),
  ...(r.finishedAt !== null ? { finishedOn: r.finishedAt } : {}),
});

export function toVehicle(v: WireVehicle): Vehicle {
  const dueIn = v.nextServiceDate ?? "—";
  const overdue =
    v.nextServiceDate !== null && v.nextServiceDate < new Date().toISOString().slice(0, 10);
  return {
    id: v.id,
    name: v.nickname ?? `${v.year ?? ""} ${v.make} ${v.model}`.trim(),
    plate: v.licensePlate ?? "",
    color: v.color ?? "",
    odometer: v.currentOdometerKm ?? 0,
    fuelMpg: 0, // no backend field — CONTRACT_DIFF row (DECISION-PENDING)
    fuelUnit: "L/100km",
    nextService: {
      name: v.nextServiceType ?? "Service",
      dueIn,
      urgency: overdue ? "overdue" : "ok",
    },
    upcoming: [],
    history: [], // service log endpoint exists; wiring is a follow-up row
  };
}

const SHOPPING_CATEGORY: Record<string, ShoppingCategory> = {
  produce: "Produce",
  pantry: "Pantry",
  dairy: "Dairy & bread",
  household: "Household",
  kids: "Kids",
  pharmacy: "Pharmacy",
};

export const toShoppingItem = (s: WireShopping): ShoppingItem => ({
  id: s.id,
  name: s.name,
  done: s.isPurchased,
  category: SHOPPING_CATEGORY[(s.category ?? "").toLowerCase()] ?? "Pantry",
  ...(s.quantity ? { note: s.quantity } : {}),
});

export const toVaultItem = (v: WireVault): VaultItem => ({
  id: v.id,
  title: v.name,
  kind: "document", // backend vault is document-metadata only (KMS/GCS pending)
  category:
    v.category === "insurance"
      ? "Money"
      : v.category === "travel"
        ? "Travel"
        : v.category === "health"
          ? "Health"
          : "Family",
  updated: v.expiryDate ? `expires ${v.expiryDate}` : "",
});

/** Series computed at the boundary from rows — never stored (ruling). */
export function toHealthMetrics(records: WireHealthRecord[]): HealthMetric[] {
  const byMetric = new Map<string, WireHealthRecord[]>();
  for (const r of records) {
    const list = byMetric.get(r.metricType) ?? [];
    list.push(r);
    byMetric.set(r.metricType, list);
  }
  const out: HealthMetric[] = [];
  for (const [metric, rows] of byMetric) {
    const sorted = rows.slice().sort((a, b) => a.recordDate.localeCompare(b.recordDate));
    const latest = sorted[sorted.length - 1]!;
    out.push({
      label: metric,
      value: latest.valueNumeric !== null ? String(latest.valueNumeric) : (latest.valueText ?? ""),
      unit: latest.unit ?? "",
      history: sorted.map((r) => r.valueNumeric ?? 0),
    });
  }
  return out;
}

export const toReminder = (a: WireAppointment): Reminder => ({
  id: a.id,
  title: a.provider + (a.specialty ? ` · ${a.specialty}` : ""),
  time: fmtWhen(a.startsAt),
  kind: "reminder",
  done: a.status === "completed",
});

/** Prefs: full passthrough — all four fields backend-owned (ruling 2). */
export const toPrefs = (p: WirePrefs): NotificationPrefs => ({
  smsEnabled: p.smsEnabled,
  pushEnabled: p.pushEnabled,
  digestCadence: p.digestCadence,
  localSuggestions: p.localSuggestionsEnabled,
});

export function toBriefing(b: WireBriefing): Briefing {
  return {
    // Backend sends the full greeting ("Good day, Brent"); their UI wants
    // the bare name. CONTRACT_DIFF row: greetingName is a trivial
    // server-side absorb candidate.
    greetingName: b.greeting.replace(/^good day,?\s*/i, "").trim(),
    todayLabel: b.todayLabel,
    quote: { text: b.quote.quoteText, author: b.quote.author },
    glance: {
      timeLabel: b.todayLabel,
      // Weather/search are null until their Vertex/OpenWeather sessions;
      // honest placeholders, never fixture data posing as live.
      weather: b.weather
        ? { ...b.weather, city: "" }
        : { tempC: 0, condition: "Weather arrives at launch", high: 0, low: 0, city: "" },
      search: b.groundedSearch
        ? { topic: b.groundedSearch.headline, summary: b.groundedSearch.summary }
        : { topic: "", summary: "" },
      agentSuggestions: [],
    },
    nudges: b.nudges.map(toNudge),
  };
}

// ── Goals + spending (Money screen) ─────────────────────────────────────────

export interface WireGoal {
  id: string;
  title: string;
  currentValue: number;
  targetValue: number | null;
  unit: string | null;
  targetDate: string | null;
  percentComplete: number;
}

export const toGoal = (g: WireGoal): Goal => ({
  id: g.id,
  name: g.title,
  progress: g.currentValue,
  target: g.targetValue ?? Math.max(g.currentValue, 1),
  unit: g.unit ?? "",
  targetDate: g.targetDate ?? "",
});

export interface WireTransaction {
  id: string;
  postedOn: string;
  amountCents: number;
  merchant: string | null;
  category: string;
}

export interface SpendingView {
  monthLabel: string;
  totalCents: number;
  rows: SpendingRow[];
  recentLog: { id: string; label: string; when: string; amountCents: number }[];
}

/** Month-to-date picture computed from transactions at the boundary. */
export function toSpending(transactions: WireTransaction[]): SpendingView {
  const monthStart = new Date();
  monthStart.setDate(1);
  const startIso = monthStart.toISOString().slice(0, 10);
  const monthLabel = monthStart.toLocaleDateString("en-CA", { month: "long", year: "numeric" });

  const expenses = transactions.filter((t) => t.amountCents < 0 && t.postedOn >= startIso);
  const totalCents = expenses.reduce((s, t) => s - t.amountCents, 0);

  const byCategory = new Map<string, number>();
  for (const t of expenses) {
    byCategory.set(t.category, (byCategory.get(t.category) ?? 0) - t.amountCents);
  }
  const rows: SpendingRow[] = [...byCategory.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([category, amountCents]) => ({
      category,
      amountCents,
      pct: totalCents > 0 ? Math.round((amountCents / totalCents) * 100) : 0,
    }));

  const recentLog = transactions.slice(0, 8).map((t) => ({
    id: t.id,
    label: t.merchant ?? t.category,
    when: t.postedOn,
    amountCents: Math.abs(t.amountCents),
  }));

  return { monthLabel, totalCents, rows, recentLog };
}

// ── Webhooks (Settings → API) ────────────────────────────────────────────────

export interface WireWebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  isActive: boolean;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  failureCount: number;
}

export const toWebhookRecord = (w: WireWebhookEndpoint): WebhookRecord => ({
  id: w.id,
  url: w.url,
  events: w.events,
  status: w.failureCount > 0 ? "failing" : "healthy",
  lastDelivery: w.lastSuccessAt ?? w.lastFailureAt ?? "—",
});

// ── Legacy letters ───────────────────────────────────────────────────────────

export interface WireLegacyItem {
  id: string;
  itemKind: string;
  title: string;
  createdAt: string;
}

export const toLegacyLetter = (l: WireLegacyItem): LegacyLetter => ({
  id: l.id,
  title: l.title,
  date: l.createdAt.slice(0, 10),
});

// ── Family members ───────────────────────────────────────────────────────────

export interface WireMember {
  id: string;
  name: string;
  relationship: string;
  dateOfBirth: string | null;
  // Profile fields (GET /life/family/members returns them; older callers
  // only read the four above, so they stay optional here).
  phone?: string | null;
  email?: string | null;
  isElderCare?: boolean;
  checkInEnabled?: boolean;
}

/** Presentation initials for a member profile card (same rule as toFamily). */
export function memberInitials(name: string): string {
  return initialsOf(name);
}

/** Presentation color for the i-th member (same palette as toFamily). */
export function memberColor(i: number): string {
  return MEMBER_COLORS[i % MEMBER_COLORS.length]!;
}

const KID_RELATIONSHIPS = new Set(["son", "daughter", "child", "kid"]);
const MEMBER_COLORS = [
  "var(--dryvn-primary)",
  "var(--dryvn-warm-deep)",
  "var(--dryvn-sky-deep)",
  "var(--dryvn-primary-deep)",
  "var(--dryvn-grey)",
];

function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function ageOf(dob: string | null): number {
  if (!dob) return 0;
  const ms = Date.now() - new Date(dob).getTime();
  return Math.max(0, Math.floor(ms / (365.25 * 86_400_000)));
}

/** initials/colors are presentation-only — synthesized, never stored. */
export function toFamily(members: WireMember[]): Family {
  const parents: Parent[] = [];
  const kids: Kid[] = [];
  members.forEach((m, i) => {
    const base = {
      id: m.id,
      name: m.name,
      initials: initialsOf(m.name),
      color: MEMBER_COLORS[i % MEMBER_COLORS.length]!,
    };
    if (KID_RELATIONSHIPS.has(m.relationship.toLowerCase())) {
      kids.push({ ...base, age: ageOf(m.dateOfBirth) });
    } else {
      parents.push(base);
    }
  });
  return { parents, kids };
}

// ── tiny formatters ──────────────────────────────────────────────────────────

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtDuration(startIso: string, endIso: string): string {
  const mins = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000);
  if (mins <= 0) return "";
  return mins % 60 === 0 ? `${mins / 60}h` : `${mins}m`;
}
